import { useCallback, useRef, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { CheckoutProduct, type CheckoutProductProps } from "@baluu/ui/components/checkout";
import { productBasketLine, type ProductCheckoutResult } from "@baluu/ui/lib/checkout";
import { basketModeFor, parseBasketMode } from "@baluu/ui/lib/checkout/basket-surface";
import { useBasketItemCount } from "~/widgets/lib/use-basket";
import { fetchProductPage } from "./fetchProductPage";
import { useRequest } from "../PurchaseOverlay/useRequest";
import { getQueryClient } from "~/widgets/lib/query-client";
import {
  addPurchaseToBasket,
  closePurchaseOverlay,
  proceedToBasketCheckout,
  purchaseApiBase,
} from "~/widgets/lib/purchase-overlay";
import { TermsDialogPortal } from "../TermsDialogPortal";
import {
  PurchaseOverlayRetry,
  PurchaseOverlayShell,
  PurchaseOverlaySkeleton,
  useSchoolCheckoutConfig,
} from "../PurchaseOverlay";
import { useBasketConflict } from "~/widgets/lib/use-basket-conflict";
import type { CustomTerms, TermsSettings } from "../PurchaseOverlay/useSchoolCheckoutConfig";

export type ProductCheckoutOverlayProps = {
  businessId: string;
  businessSlug: string;
  apiUrl?: string;
  primaryColor?: string;
  /** The product's slug, as its hosted page /product/<slug> names it. */
  productSlug: string;
  /** The surface's basket (`BasketMode`): "on" or "page" offers "Add to
   *  basket" beside "Proceed to checkout" (the flow's basket choice step). */
  basket?: string;
};

/**
 * <baluu-product-checkout-overlay>: buy a product on the page, as the hosted
 * booking site's /product/<slug> page does. The product and its postage
 * options from `GET /widget/pages/product/:slug`, through @baluu/ui's
 * CheckoutProduct (quantity, postage, gifting, the product's questions, the
 * buyer's details) — the same flow the hosted page opens. Its result becomes
 * the hosted page's basket line (@baluu/ui `productBasketLine`) in the basket
 * checkout, which collects the delivery address, applies a discount code and
 * takes payment.
 */
// `businessSlug` is taken (the element's theme reads it) but not read here.
export function ProductCheckoutOverlay({
  businessId,
  apiUrl,
  primaryColor = "#03c2b9",
  productSlug,
  basket,
}: ProductCheckoutOverlayProps) {
  const queryClient = getQueryClient();
  const base = purchaseApiBase(apiUrl);
  const school = useSchoolCheckoutConfig(businessId, apiUrl);
  const {
    data: page,
    failed,
    retry: reload,
  } = useRequest(`${base}/widget/pages/product/${productSlug}`, () => fetchProductPage(base, productSlug));
  const settings = school.status === "ready" ? school.config : null;
  const mode = settings ? basketModeFor(parseBasketMode(basket), settings.basket?.isEnabled) : "off";
  // A refused "Add to basket" / "Proceed to checkout", shown on the flow's
  // choice step until the visitor moves to another step.
  const { conflict: basketConflict, refuse, onStepChange: conflictOnStepChange } = useBasketConflict({
    businessId,
    apiUrl,
    primaryColor,
    mode,
  });
  const basketItemCount = useBasketItemCount(businessId);
  const scroller = useRef<HTMLDivElement>(null);
  const handleStepChange = useCallback(
    (step: string) => {
      conflictOnStepChange(step);
      scroller.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [conflictOnStepChange],
  );
  const [terms, setTerms] = useState<{ isOpen: boolean; termsSettings?: TermsSettings; customTerms?: CustomTerms }>({
    isOpen: false,
  });


  const close = useCallback(() => closePurchaseOverlay("product"), []);

  const proceed = useCallback(
    (result: ProductCheckoutResult) => {
      // The basket line carries the school's name: only once it's known.
      if (!page || !settings) return;
      const line = productBasketLine({
        product: page.product,
        postages: page.postages,
        result,
        school: { _id: businessId, name: settings.name },
      });
      const outcome = proceedToBasketCheckout({
        line,
        businessId,
        apiUrl,
        primaryColor,
        mode,
        close,
      });
      if (!outcome.ok) refuse([line]);
    },
    [page, settings, businessId, apiUrl, primaryColor, mode, close, refuse],
  );

  const addToBasket = useCallback(
    (result: ProductCheckoutResult) => {
      if (!page || !settings || mode === "off") return;
      const line = productBasketLine({
        product: page.product,
        postages: page.postages,
        result,
        school: { _id: businessId, name: settings.name },
      });
      const outcome = addPurchaseToBasket({
        line,
        businessId,
        apiUrl,
        primaryColor,
        mode,
        close,
      });
      if (!outcome.ok) refuse([line]);
    },
    [page, settings, businessId, apiUrl, primaryColor, mode, close, refuse],
  );

  // The flow waits on the school's settings as well as the product: its
  // terms, and the name on the basket line.
  if (!page || !settings) {
    return (
      <PurchaseOverlayShell title="Shop" onClose={close}>
        {school.status === "error" ? (
          <PurchaseOverlayRetry onRetry={school.retry}>We couldn&apos;t load the shop&apos;s settings.</PurchaseOverlayRetry>
        ) : failed ? (
          <PurchaseOverlayRetry onRetry={reload}>We couldn&apos;t load this product. It may no longer be on sale.</PurchaseOverlayRetry>
        ) : (
          <PurchaseOverlaySkeleton />
        )}
      </PurchaseOverlayShell>
    );
  }

  const { product, postages } = page;
  const postageOptions: CheckoutProductProps["postageOptions"] = postages.map((p) => ({
    id: p._id || p.id || "",
    name: p.name || "",
    description: p.description,
    price: p.price ?? 0,
    estimatedDays: p.estimatedDays,
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <div
        ref={scroller}
        className="checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50"
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
      >
        <CheckoutProduct
          product={product}
          postages={postages}
          postageOptions={postageOptions}
          discount={product.discount}
          onClose={close}
          onStepChange={handleStepChange}
          currency={product.currency || "GBP"}
          timezone={settings.timezone}
          cancellationPolicyUrl={settings.cancellationPolicyUrl}
          termsSettings={settings.termsSettings}
          customTerms={settings.customTerms ?? undefined}
          onOpenTermsDialog={(data) => setTerms({ isOpen: true, ...data })}
          school={{
            _id: businessId,
            name: settings.name || "",
            isNewsletterSignupEnabled: settings.isNewsletterSignupEnabled,
            businessCountry: settings.businessCountry,
          }}
          // With the basket on, the flow ends on its choice step ("Add to
          // basket" or "Proceed to checkout"); off, one product goes straight
          // on to payment, as a class does.
          basketItemCount={basketItemCount}
          basketConflict={basketConflict}
          onAddToBasket={mode === "off" ? undefined : addToBasket}
          onProceedToBasketCheckout={proceed}
          directCheckout={mode === "off"}
        />
      </div>
      <TermsDialogPortal
        isOpen={terms.isOpen}
        onClose={() => setTerms((prev) => ({ ...prev, isOpen: false }))}
        settings={terms.termsSettings}
        customTerms={terms.customTerms}
        primaryColor={primaryColor}
      />
    </QueryClientProvider>
  );
}
